import { VonqContractChannelDetails } from 'api-clients/monolith';
import { useEffect, useMemo } from 'react';
import { Controller, type FieldPath } from 'react-hook-form';
import { useSearchParams, useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import InfoIcon from '@/assets/icons/information.svg';
import { Button } from '@/components/ui/button';
import { ChannelIcon } from '@/components/ui/channel-icon';
import { Input } from '@/components/ui/input';
import { RichText } from '@/components/ui/rich-text';
import { FormSelect } from '@/components/ui/select';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';

import { useAddContractContext } from '@/contexts/add-contract-context';
import { OAUTH_CALLBACK_HASH_PARAM } from '@/hooks/use-add-contract-search-params';

import type { ContractForm, ContractFormFields } from './types';

const credentialLabelTooltipRichTextClassName =
  'prose-invert max-w-none prose-p:my-0 prose-a:underline';

type ContractCredential =
  VonqContractChannelDetails['contract_credentials'][number];

interface CredentialFieldsProps {
  hookForm: ContractForm;
  logoSquareUrl: string | null;
  credentials: ContractCredential[];
}

interface ContractCredentialInputFieldProps {
  field: ContractCredential;
  control: ContractForm['control'];
  error?: string;
}

function ContractCredentialInputField({
  field,
  control,
  error,
}: ContractCredentialInputFieldProps) {
  const intl = useIntl();
  const credentialPath =
    `credentials.${field.name}` as FieldPath<ContractFormFields>;

  const selectOptions = useMemo(
    () =>
      (field.options ?? []).map(option => ({
        label: option.label,
        value: option.key,
      })),
    [field.options],
  );
  const hasSelectOptions = selectOptions.length > 0;
  const description = field.description ?? '';
  const displayLabel =
    field.label.length > 0
      ? `${field.label.charAt(0).toUpperCase()}${field.label.slice(1)}`
      : field.label;

  const fieldLabel = (
    <label className="flex items-center gap-1">
      <Typography variant="subtitle2">
        {displayLabel} <span className="text-fx-rose">*</span>
      </Typography>
      {description && (
        <Tooltip
          content={
            <RichText
              html={description}
              openLinksInNewTab
              className={credentialLabelTooltipRichTextClassName}
            />
          }
          className="pointer-events-auto"
        >
          <img src={InfoIcon} alt="" aria-hidden className="h-4 w-4" />
        </Tooltip>
      )}
    </label>
  );

  if (hasSelectOptions) {
    return (
      <div className="flex w-full flex-col gap-1">
        {fieldLabel}
        <FormSelect
          control={control}
          name={credentialPath}
          placeholder={intl.formatMessage({
            defaultMessage: 'Select an option',
            description:
              'Add contract credentials: empty option in select field',
          })}
          options={selectOptions}
          isMultiSelect={false}
          error={error}
        />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-1">
      {fieldLabel}
      <Controller
        control={control}
        name={credentialPath}
        render={({ field: rhfField }) => (
          <Input
            id={field.name}
            name={rhfField.name}
            error={error}
            className="w-full"
            onBlur={rhfField.onBlur}
            ref={rhfField.ref}
            value={typeof rhfField.value === 'string' ? rhfField.value : ''}
            onChange={rhfField.onChange}
          />
        )}
      />
    </div>
  );
}

export function CredentialFields({
  hookForm,
  logoSquareUrl,
  credentials,
}: CredentialFieldsProps) {
  const [searchParams] = useSearchParams();
  const { channelId, channelName } = useAddContractContext();
  const {
    control,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = hookForm;

  const credentialValues = watch('credentials');
  const location = useLocation();

  const authField = useMemo(
    () => credentials.find(field => field.url),
    [credentials],
  );

  const inputFields = useMemo(
    () => credentials.filter(field => !field.url),
    [credentials],
  );

  useEffect(() => {
    if (authField == null || channelId == null) return;

    const hashParam = searchParams.get(OAUTH_CALLBACK_HASH_PARAM);
    if (!hashParam?.trim()) return;

    const hashValue = decodeURIComponent(hashParam);

    if ((credentialValues[authField.name] ?? '') !== '') return;

    setValue(
      'credentials',
      { ...getValues('credentials'), [authField.name]: hashValue },
      { shouldValidate: false },
    );
  }, [
    authField,
    channelId,
    credentialValues,
    getValues,
    searchParams,
    setValue,
  ]);

  const requiresAuthorization = authField?.url != null;
  const oauthCredentialValue = authField
    ? (credentialValues[authField.name] ?? '')
    : '';
  const isAuthorized = !requiresAuthorization || oauthCredentialValue !== '';

  const handleAuthorize = () => {
    const oauthUrl = authField?.url;

    if (oauthUrl) {
      const callbackUrl = `${window.location.origin}${location.pathname}${location.search}`;
      const separator = oauthUrl.includes('?') ? '&' : '?';
      const finalUrl = `${oauthUrl}${separator}callbackUrl=${encodeURIComponent(
        callbackUrl,
      )}`;
      window.location.href = finalUrl;
    }
  };

  if (credentials.length === 0 || channelId == null) return null;

  return (
    <div className="flex flex-col items-start gap-4 self-stretch">
      {requiresAuthorization && authField && (
        <div className="border-fx-neutral-200 bg-fx-neutral-50 flex items-center gap-3 self-stretch rounded-sm border p-4">
          <ChannelIcon
            channelName={channelName}
            logoSquareUrl={logoSquareUrl}
          />
          <Typography variant="small2" className="min-w-0 flex-1">
            <FormattedMessage
              defaultMessage="Authorise Fountain to access {channelName}"
              description="Add contract sheet: heading above OAuth authorisation button; {channelName} is the job channel name"
              values={{ channelName: channelName }}
            />
          </Typography>
          <Button
            type="button"
            className="shrink-0 uppercase"
            onClick={handleAuthorize}
            disabled={isAuthorized}
          >
            <FormattedMessage
              defaultMessage="Initialize Authentication"
              description="Add contract sheet: opens job channel authorisation URL in a new tab"
            />
          </Button>
        </div>
      )}

      {isAuthorized &&
        inputFields.map(field => (
          <ContractCredentialInputField
            key={field.name}
            field={field}
            control={control}
            error={errors.credentials?.[field.name]?.message}
          />
        ))}
    </div>
  );
}
